import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import { setOrderDataAction, setOrderTypeAction } from '../../redux/Actions/CreateOrderActions';
import { resetCreateOrderFlow } from '../../redux/Reducers/CreateOrderReducer';

// Images
import lc_onetime from '../../images/lc_onetime_new.png';
import lc_subscription from '../../images/lc_subscription_new.png';
import pl_onetime from '../../images/pl_onetime.png';
import pl_subscription from '../../images/pl_subscription.png';

// ─── Constants ──────────────────────────────────────────────────────────────────
const FREQ_OPTIONS = [
  { name: 'Weekly', value: 'WEEK' },
  { name: 'Every-other-week', value: 'BIWEEKLY' },
  { name: 'Monthly', value: 'MONTH' },
];

const ORDER_TYPES = [
  {
    key: 'oneTime',
    label: 'One-Time Order',
    description: 'Schedule a single pickup & delivery',
    images: { laundrycare: lc_onetime, pushlaundry: pl_onetime },
  },
  {
    key: 'subscription',
    label: 'Subscription',
    description: 'Recurring pickup on a set schedule',
    images: { laundrycare: lc_subscription, pushlaundry: pl_subscription },
  },
];

// ─── StepOrderType ──────────────────────────────────────────────────────────────
export default function StepOrderType({ onContinue }) {
  const dispatch = useDispatch();
  const { orderType, orderRepeatFrequency } = useSelector((s) => s.createNewOrder);
  const tenant = import.meta.env.VITE_TENANT_NAME;

  // Default from URL param on first mount
  useEffect(() => {
    if (!orderType) {
      const urlParams = new URLSearchParams(window.location.search);
      const t = urlParams.get('type');
      dispatch(setOrderTypeAction(t === 'subscription' ? 'subscription' : 'oneTime'));
    }
  }, []);

  const setType = (t) => {
    dispatch(resetCreateOrderFlow());
    dispatch(setOrderTypeAction(t));
  };

  const setFrequency = (f) => {
    dispatch(setOrderDataAction({ orderRepeatFrequency: { value: f.value, name: f.name } }));
  };

  return (
    <div>
      {/* Type cards */}
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
        {ORDER_TYPES.map((t) => {
          const selected = orderType === t.key;
          return (
            <div
              key={t.key}
              className={`group flex cursor-pointer items-center gap-4 rounded-xl border-2 px-4 py-4 transition-all ${
                selected
                  ? 'border-client-300 bg-gradient-to-br from-blue-50/60 to-white shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
              onClick={() => setType(t.key)}
            >
              {/* Radio */}
              <input
                type='radio'
                name='orderType'
                checked={selected}
                readOnly
                className='text-client-300 focus:ring-client-300 h-4 w-4 shrink-0 cursor-pointer'
              />

              {/* Icon */}
              <img
                className='h-12 w-12 shrink-0 object-contain'
                src={t.images[tenant] || t.images.laundrycare}
                alt=''
              />

              {/* Text */}
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-bold text-slate-900'>{t.label}</p>
                <p className='mt-0.5 text-xs leading-snug text-slate-500'>{t.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Frequency pills — only for subscription */}
      {orderType === 'subscription' && (
        <div className='mt-4'>
          <p className='mb-2 text-xs font-semibold text-slate-500'>How often?</p>
          <div className='flex flex-wrap gap-2'>
            {FREQ_OPTIONS.map((f) => {
              const active = orderRepeatFrequency?.value === f.value;
              return (
                <button
                  key={f.value}
                  type='button'
                  className={`rounded-full border-2 px-5 py-1.5 text-xs font-semibold transition ${
                    active
                      ? 'border-client-300 bg-client-300 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  onClick={() => setFrequency(f)}
                >
                  {f.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
